/**
 * Trust-center Q&A, shared between /security and any future surfaces.
 *
 * Every answer here is verified against what Forsyt actually does today.
 * Do NOT add capabilities here that aren't real. If you can't answer
 * honestly, leave the question off the public page until it can be.
 */
import { Database, Users as UsersIcon, Lock, FileCheck, Globe, AlertTriangle, Bot, Activity } from 'lucide-react';

export const TRUST_SECTIONS = [
  {
    id: 'data-handling',
    icon: Database,
    title: 'Data handling',
    summary: 'Where data lives, how it is encrypted, and what we do, and do not, do with it.',
    qa: [
      {
        q: 'Where is our data physically stored?',
        a: 'EU and UK customer data is stored in AWS Frankfurt (eu-central-1). Indian and APAC customer data is stored in AWS Mumbai (ap-south-1). North American customers can opt for AWS Virginia (us-east-1). Customer choice is set during onboarding and is contractually fixed thereafter.',
      },
      {
        q: 'Is data encrypted at rest? With what?',
        a: 'Yes. AES-256 encryption at rest using AWS KMS-managed keys.',
      },
      {
        q: 'Is data encrypted in transit?',
        a: 'Yes. TLS 1.3 for all client-server traffic. TLS 1.2+ for service-to-service traffic inside our VPC. HSTS enforced on all customer-facing endpoints.',
      },
      {
        q: 'Do you train AI models on our data?',
        a: 'No. Forsyt uses Anthropic Claude (Sonnet) for AI Deal Chat reasoning. Anthropic\'s commercial terms guarantee zero training on prompts or responses sent through their API. Our backend processes prompts in-memory only, nothing is persisted to Forsyt servers after the response is returned.',
      },
      {
        q: 'How long is data retained? What is your deletion process?',
        a: 'Customer pipeline data is retained for the duration of the contract. AI conversation context is in-memory only, discarded at session end. Application logs are retained for as long as operationally needed and then purged. On contract termination, all customer data is deleted within 30 days. A signed deletion certificate is provided on request.',
      },
      {
        q: 'What happens to our data if we terminate?',
        a: 'You receive an export of your pipeline data within 5 business days of the termination notice (CSV / JSON / direct CRM write-back). 30 days post-termination, we purge our active store. 60 days post-termination, we purge all backup copies. Deletion certificate provided.',
      },
      {
        q: 'Will our data leave the EU/EEA?',
        a: 'For EU/UK customers: no. Application processing, AI inference (Anthropic EU residency), and storage all stay in eu-central-1 / EU. The only exception is engineer access for support, governed by Standard Contractual Clauses and minimised by least-privilege RBAC.',
      },
      {
        q: 'Do you anonymize or pseudonymize personal data?',
        a: 'Personal data inside customer pipelines is processed as-is (we are the processor, you are the controller, pseudonymization is your choice as controller). Internal product analytics use a one-way hash of user ID and never include pipeline data.',
      },
    ],
  },
  {
    id: 'sub-processors',
    icon: UsersIcon,
    title: 'Sub-processors & vendor management',
    summary: 'Every third party that touches your data, what they do, and where they live.',
    qa: [
      {
        q: 'Provide a list of all sub-processors',
        a: 'See /sub-processors for the live list with vendor, purpose, data accessed, hosting region, and DPA link for each. Notable entries: AWS (hosting), MongoDB Atlas (database), Anthropic (LLM), Slack (internal lead-capture), PostHog (anonymized analytics).',
      },
      {
        q: 'For each sub-processor: what data, what purpose, where hosted?',
        a: 'AWS: encrypted hosting (EU/IN/US). MongoDB Atlas: encrypted DB cluster (same regions). Anthropic: prompt-only LLM inference (zero retention, EU residency available). Slack: lead-capture webhook only (does not receive customer pipeline data). PostHog: anonymized product telemetry (EU). GA/GTM: marketing-site analytics only, post-consent.',
      },
      {
        q: 'Will you notify us before adding a new sub-processor?',
        a: 'Yes. 30-day advance notice via email (and in-app banner) before any new sub-processor goes live. You have a contractual right to object; objection triggers a good-faith remediation discussion.',
      },
    ],
  },
  {
    id: 'access',
    icon: Lock,
    title: 'Access controls & internal security',
    summary: 'Who at Forsyt can touch your data, and how we make sure they never do without reason.',
    qa: [
      {
        q: 'Who at your company can access our data?',
        a: 'Production data access is restricted to the founder and the designated technical lead. All access is least-privilege and time-bounded, engineers do not have standing access to production. Production access events are logged for audit.',
      },
      {
        q: 'Do you use SSO internally?',
        a: 'Yes. Forsyt operator access to production systems is gated by Google Workspace SSO. There is no shared or password-only access path to production infrastructure.',
      },
      {
        q: 'Do you log all access to customer data?',
        a: 'Yes. Application-level access logs (who, what, when, why) are retained for audit. AWS CloudTrail logs cloud-infrastructure events.',
      },
      {
        q: 'Do you do background checks on engineers?',
        a: 'For permanent engineering hires with production access, we will run standard employment background checks (employment and education verification) prior to provisioning access. As a small team we will share our hiring screening posture with you during pilot scoping.',
      },
    ],
  },
  {
    id: 'compliance',
    icon: FileCheck,
    title: 'Compliance & certifications',
    summary: 'What we hold today, and what we deliberately do not claim until it is true.',
    qa: [
      {
        q: 'Are you ISO 27001 certified?',
        a: 'Yes. Forsyt is ISO/IEC 27001:2022 certified. Our Information Security Management System (ISMS) covers the design, development, hosting, and operation of the Forsyt Revenue OS platform and supporting business functions. The certificate, scope statement, and Statement of Applicability (SoA) summary are available on request under NDA.',
      },
      {
        q: 'Are you GDPR compliant?',
        a: 'Yes. We are a GDPR-compliant data processor. Lawful basis, sub-processors, data residency (AWS Frankfurt for EU), 72-hour breach notification, and the full set of data subject rights are documented above (see GDPR section). DPA available on request to privacy@forsyt.com.',
      },
      {
        q: 'Do you have a Master Services Agreement (MSA) we can sign?',
        a: 'Yes. Forsyt maintains an MSA template covering the commercial framework, Order Forms, fees, IP, liability, termination, governing law (India / New Delhi), and exhibits for the DPA, NDA, and SLA. We share it with enterprise prospects on request to legal@forsyt.com after a mutual NDA is in place. We are happy to redline against your paper if you prefer to lead with your template.',
      },
      {
        q: 'Have you ever had a data breach?',
        a: 'No. There has been no confirmed breach of customer personal data since Forsyt was founded. If one ever occurs, our breach notification process notifies affected customers and supervisory authorities within 72 hours.',
      },
    ],
  },
  {
    id: 'gdpr-detail',
    icon: Globe,
    title: 'GDPR-specific',
    summary: 'Our processor commitments, your data subject rights, and how to exercise them.',
    qa: [
      {
        q: 'Are you a controller or processor?',
        a: 'Forsyt is a data processor for the customer pipeline data we process on your behalf. You are the data controller. Forsyt is a data controller only for our own marketing-site visitors and lead-form submissions, covered separately in our public Privacy Policy.',
      },
      {
        q: 'Do you have a DPA we can sign?',
        a: 'Yes. Email privacy@forsyt.com or click "Request our DPA" above and we will share it within one business day. Standard Contractual Clauses are annexed for any non-EEA transfers.',
      },
      {
        q: 'How do you handle data subject access requests (DSARs)?',
        a: 'For requests originating with you (our customer): we work with you to fulfil them, your admin can export, rectify, or delete records via the application, and we assist on anything not yet self-serve. For requests originating from a data subject directly to Forsyt: we forward to you (the controller) within 5 business days and assist as required.',
      },
      {
        q: 'What is your breach notification SLA?',
        a: '72 hours from confirmed awareness of any personal-data breach, per GDPR Art. 33. We notify the controller (you) and the relevant supervisory authority.',
      },
      {
        q: 'Who is your DPO?',
        a: 'Udit Pandoh (Founder) acts as Data Protection Officer for Forsyt. Reachable at privacy@forsyt.com. As we scale we may designate a dedicated external DPO; you will be notified contractually if that changes.',
      },
      {
        q: 'Do you do Data Protection Impact Assessments (DPIAs)?',
        a: 'For any new processing activity classified as high-risk under GDPR Art. 35 (e.g., processing of special-category data, large-scale monitoring), we will work with you on a customer-specific DPIA scoped to the activity. Our internal DPIA approach is in development; we will share its current form on request.',
      },
      {
        q: 'Are international transfers covered?',
        a: 'EU customers: data does not leave the EEA by default. For any incidental transfer (e.g., engineer access from outside EEA for support), Standard Contractual Clauses apply with supplementary technical measures (encryption at rest, encryption in transit, role-based access).',
      },
      {
        q: 'How do data subjects lodge a complaint?',
        a: 'Data subjects may complain to their national supervisory authority (e.g., the UK ICO at ico.org.uk, or any EEA data protection authority). They may also write to privacy@forsyt.com, we will engage in good faith.',
      },
    ],
  },
  {
    id: 'incident',
    icon: AlertTriangle,
    title: 'Incident response',
    summary: 'What we do when something goes wrong, and how often we drill it.',
    qa: [
      {
        q: 'Do you have an incident response process?',
        a: 'Yes, a documented incident-response process covering detection, containment, eradication, recovery, lessons-learned, and customer / regulator notification within the 72-hour GDPR window. The runbook is in active development and will be shareable in redacted form under NDA as it matures.',
      },
      {
        q: 'How do you detect security incidents?',
        a: 'AWS-native security signals (CloudTrail, VPC flow logs, IAM anomaly alerts), application-level audit logs, dependency-scan alerts, and customer-reported issues via privacy@forsyt.com. We are evaluating AWS GuardDuty and a managed SOC for the next scale stage.',
      },
      {
        q: 'Do you run security exercises?',
        a: 'The founder and the designated technical lead run informal walk-throughs of incident scenarios. As the team grows, this becomes a formal cross-functional tabletop on a defined cadence.',
      },
      {
        q: 'List security incidents in the past 12 months',
        a: 'No customer-impacting security incidents to date. If one ever occurs, our breach notification process notifies affected customers and supervisory authorities within 72 hours.',
      },
    ],
  },
  {
    id: 'ai-llm',
    icon: Bot,
    title: 'AI / LLM specific',
    summary: 'Newer questions every modern security review now includes, answered honestly.',
    qa: [
      {
        q: 'Which LLM do you use?',
        a: 'Anthropic Claude (Sonnet model family). We chose Anthropic specifically because their commercial terms include zero training on customer prompts/responses, EU data residency is available, they have a public DPA, and they participate in the EU AI Act compliance framework. Model can be switched or co-located on customer infrastructure for Enterprise deployments.',
      },
      {
        q: 'Is the LLM hosted by you or a 3rd party?',
        a: 'Hosted by Anthropic via their public API today. For Enterprise contracts we are open to scoping alternative deployments (e.g., Anthropic via AWS Bedrock in a dedicated VPC, or evaluating self-hosted open-source models inside your infrastructure), selection happens during contracting and is subject to mutual scoping.',
      },
      {
        q: 'Is there a Data Processing Addendum with Anthropic?',
        a: 'Yes. Anthropic\'s commercial DPA covers our use of their API. Their DPA includes SCCs for non-EEA transfers and zero-training commitments. Available for review on request.',
      },
      {
        q: 'Can the LLM regenerate our data when prompted by another customer?',
        a: 'No. Two reasons: (1) Anthropic does not train on customer API prompts, so your data does not enter their model weights; (2) every customer\'s session is isolated, prompts include only your tenant\'s context, never another customer\'s data.',
      },
      {
        q: 'Are you transparent about AI vs. human decisions?',
        a: 'Yes. Every AI-generated insight in Forsyt is labelled as such and cites its source data (call, email, CRM event, timestamp). Decisions that materially affect a deal, close-date push, status change, are always reviewed by a human before being written back to your CRM.',
      },
    ],
  },
  {
    id: 'ops',
    icon: Activity,
    title: 'Operational',
    summary: 'Uptime, recovery, and the boring-but-critical operational metrics.',
    qa: [
      {
        q: 'What is your uptime target?',
        a: 'Uptime targets are defined per Enterprise order form, with service credits negotiated as part of the contract. Our internal target is 99.9% monthly availability on the production platform.',
      },
      {
        q: 'How is uptime measured?',
        a: 'Continuous synthetic and live monitoring on all production endpoints. We share monthly availability summaries with Enterprise customers on request. A public status page is in preparation.',
      },
      {
        q: 'Disaster recovery, RTO and RPO?',
        a: 'Continuous backups with point-in-time restore in MongoDB Atlas. Recovery targets are documented internally and negotiated per Enterprise order form.',
      },
      {
        q: 'Vulnerability management, CVE patch SLAs?',
        a: 'Critical and high-severity CVEs are prioritised and patched promptly. Lower-severity vulnerabilities are addressed in the standard release cycle. Automated dependency scanning runs against the codebase.',
      },
    ],
  },
];
